import React from 'react';
import { useGetFullPortfolio, useGetFullPortfolioByName } from '../services/api/users-api';
import SabriTemplate from '../templates/sabri-template/sabri-template';
import Loading from '../components/loading';
import ClassicTemplate from '../templates/classic-template/classic-template';
import BandaraTemplate from '../templates/bandara-template/bandara-template';
import OpusTemplate from '../templates/opus-template/opus-template';
import MenuIcon from '../components/menu-icon';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import styled from 'styled-components';
import { useUser } from '../services/contexts/user-context';

const CopyButtonContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ViewCountBadge = styled.div`
  background: var(--bg-card);
  color: var(--text-main);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  min-width: 80px;
`;

const CopyButton = styled(Button)`
  background: var(--btn-primary) !important;
  border: none !important;
  border-radius: 50px !important;
  padding: 12px 20px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: var(--btn-primary-hover) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
  }
  
  .p-button-label {
    color: white !important;
    font-weight: 600 !important;
  }
`;

export default function Portfolio() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile_name } = useParams(); // optional route param
  const toastRef = React.useRef(null);
  const { user } = useUser();
  
  // Call both hooks, but only enable the one we need
  const portfolioById = useGetFullPortfolio({
    enabled: !profile_name, // Only run if no profile_name
  });

  const portfolioByName = useGetFullPortfolioByName(profile_name, {
    enabled: !!profile_name, // Only run if profile_name exists
  });

  // Pick the active query result directly — no local state layer
  const profile = profile_name ? portfolioByName.data : portfolioById.data;
  const isLoadingPortfolio = profile_name ? portfolioByName.isLoading : portfolioById.isLoading;
  const fetchPortfolioError = profile_name ? portfolioByName.error : portfolioById.error;
  const copyPortfolioLink = () => {
    // Generate the correct portfolio URL using the account username
    const baseUrl = window.location.origin;
    const username = profile?.profile?.username;
    
    if (!username) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Username not found',
        life: 3000
      });
      return;
    }
    
    // Encode the username for URL
    const encodedUsername = encodeURIComponent(username);
    const portfolioUrl = `${baseUrl}/profile/${encodedUsername}`;
    
    navigator.clipboard.writeText(portfolioUrl).then(() => {
      toastRef.current.show({
        severity: 'success',
        summary: 'Link Copied!',
        detail: 'Portfolio link has been copied to clipboard',
        life: 3000
      });
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = portfolioUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      toastRef.current.show({
        severity: 'success',
        summary: 'Link Copied!',
        detail: 'Portfolio link has been copied to clipboard',
        life: 3000
      });
    });
  };

  const currentProfileId = user?.profile?.profile_id || user?.profile?.id;
  const viewedProfileId = profile?.profile?.id;
  const isOwner = !!currentProfileId && !!viewedProfileId && String(currentProfileId) === String(viewedProfileId);

  if (isLoadingPortfolio) {
    return <Loading text='Loading Portfolio' />;
  }

  if (fetchPortfolioError) {
    return <div>Error loading portfolio: {fetchPortfolioError.message}</div>;
  }

  return (
    <>
      <Toast ref={toastRef} />

      {profile &&
        <div>
          {profile.profile.selected_template === 1 && <SabriTemplate data={profile} />}
          {profile.profile.selected_template === 2 && <ClassicTemplate data={profile} />}
          {profile.profile.selected_template === 3 && <BandaraTemplate data={profile} />}
          {profile.profile.selected_template === 4 && <OpusTemplate data={profile} />}
          {!([1, 2, 3, 4].includes(profile.profile.selected_template)) && (
            <SabriTemplate data={profile} />
          )}
        </div>
      }

      {/* View count and copy-link are owner-only controls. */}
      {profile && isOwner && (
        <CopyButtonContainer>
          <ViewCountBadge>
            👁️ {profile.profile?.view_count || 0} views
          </ViewCountBadge>
          <CopyButton
            label="Copy Portfolio Link"
            icon="pi pi-link"
            onClick={copyPortfolioLink}
          />
        </CopyButtonContainer>
      )}
    </>
  );
}
